import * as React from "react"
import { useQuery } from "@tanstack/react-query"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { ScrollArea } from "@/components/ui/scroll-area"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { api } from "@/lib/api"
import { useNotificationStore } from "@/store"
import { format } from "date-fns"
import {
  Bell,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Info,
  ShoppingCart,
  CreditCard,
  Server,
  Clock,
  Trash2,
  CheckCheck,
  Settings,
} from "lucide-react"

function NotificationCenter() {
  const { notifications, removeNotification, clearNotifications } = useNotificationStore()
  const [activeTab, setActiveTab] = React.useState("all")

  const { data: events } = useQuery({
    queryKey: ["notification-events"],
    queryFn: () => api.get("/events?limit=50"),
    refetchInterval: 5000,
  })

  const notificationTypes = {
    success: { icon: CheckCircle, color: "text-success", bg: "bg-success/10" },
    error: { icon: XCircle, color: "text-destructive", bg: "bg-destructive/10" },
    warning: { icon: AlertTriangle, color: "text-warning", bg: "bg-warning/10" },
    info: { icon: Info, color: "text-info", bg: "bg-info/10" },
  }

  const systemNotifications = [
    {
      id: 1,
      type: "warning",
      title: "Panel Offline",
      message: "Germany Panel is not responding",
      time: new Date(Date.now() - 300000),
      read: false,
    },
    {
      id: 2,
      type: "success",
      title: "Backup Completed",
      message: "Daily backup created successfully",
      time: new Date(Date.now() - 3600000),
      read: true,
    },
    {
      id: 3,
      type: "error",
      title: "Queue Failed",
      message: "3 items failed in purchase queue",
      time: new Date(Date.now() - 7200000),
      read: false,
    },
    {
      id: 4,
      type: "info",
      title: "Job Completed",
      message: "Stable config scan finished",
      time: new Date(Date.now() - 10800000),
      read: true,
    },
  ]

  const unreadCount = systemNotifications.filter((n) => !n.read).length

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Notification Center</h1>
          <p className="text-muted-foreground">System alerts and notifications</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={clearNotifications}>
            <CheckCheck className="mr-2 h-4 w-4" />
            Mark All Read
          </Button>
          <Button variant="outline">
            <Settings className="mr-2 h-4 w-4" />
            Settings
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Unread</CardTitle>
            <Bell className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{unreadCount}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Errors</CardTitle>
            <XCircle className="h-4 w-4 text-destructive" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-destructive">
              {systemNotifications.filter((n) => n.type === "error").length}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Warnings</CardTitle>
            <AlertTriangle className="h-4 w-4 text-warning" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-warning">
              {systemNotifications.filter((n) => n.type === "warning").length}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Success</CardTitle>
            <CheckCircle className="h-4 w-4 text-success" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-success">
              {systemNotifications.filter((n) => n.type === "success").length}
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="all">
        <TabsList>
          <TabsTrigger value="all">All</TabsTrigger>
          <TabsTrigger value="system">System</TabsTrigger>
          <TabsTrigger value="events">Events</TabsTrigger>
          <TabsTrigger value="alerts">Alerts</TabsTrigger>
        </TabsList>

        <TabsContent value="all" className="mt-4">
          <Card>
            <CardContent className="p-0">
              <ScrollArea className="h-[500px]">
                <div className="divide-y">
                  {systemNotifications.map((notification) => {
                    const config = notificationTypes[notification.type]
                    const Icon = config.icon
                    return (
                      <div
                        key={notification.id}
                        className={`flex items-start gap-4 p-4 hover:bg-muted/50 ${
                          !notification.read ? "bg-muted/30" : ""
                        }`}
                      >
                        <div className={`p-2 rounded-full ${config.bg}`}>
                          <Icon className={`h-5 w-5 ${config.color}`} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="font-medium">{notification.title}</p>
                            {!notification.read && (
                              <span className="h-2 w-2 rounded-full bg-primary" />
                            )}
                          </div>
                          <p className="text-sm text-muted-foreground">{notification.message}</p>
                          <p className="text-xs text-muted-foreground mt-1">
                            {format(notification.time, "HH:mm:ss")}
                          </p>
                        </div>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => removeNotification(notification.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    )
                  })}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="system" className="mt-4">
          <Card>
            <CardContent className="p-6">
              <p className="text-muted-foreground text-center">System notifications</p>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="events" className="mt-4">
          <Card>
            <CardContent className="p-0">
              <ScrollArea className="h-[500px]">
                <div className="divide-y">
                  {events?.data?.slice(0, 20).map((event) => (
                    <div key={event.id} className="flex items-start gap-4 p-4 hover:bg-muted/50">
                      <div className="p-2 rounded-full bg-info/10">
                        <Bell className="h-5 w-5 text-info" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <Badge variant="outline">{event.event}</Badge>
                          <span className="text-xs text-muted-foreground">
                            {format(new Date(event.created_at * 1000), "HH:mm:ss")}
                          </span>
                        </div>
                        <p className="text-sm text-muted-foreground mt-1 truncate">
                          {JSON.stringify(event.data)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="alerts" className="mt-4">
          <Card>
            <CardContent className="p-6">
              <p className="text-muted-foreground text-center">Active alerts</p>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  )
}

export default NotificationCenter
